import { ErrorBoundary } from '@sentry/react';
import { PatientDto, QueryFactory } from '@lmt/shared-types';
import {
  AppointmentStatusCodes,
  DictionaryRecordDto,
  AppointmentColorSettingsRuleDto,
  VisitListDto,
} from '@lmt/shared-types';

import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useErrorHandler } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import clsx from 'clsx';
import type { dxFilterBuilderField } from 'devextreme/ui/filter_builder';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { FilterBuilderLazy } from '@lmt/unicorn-shared/app/components/common/LazyWrappers/FilterBuilderLazy';
import { CustomPicker } from 'react-color';
import { StyledColorPicker } from './components/StyledColorPicker';
import { PreviewVisit } from './components/PreviewVisit';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import superjson from 'superjson';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './ColorRuleEditForm.module.scss';
import globalStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { Input } from '../../../common/ui-kit/inputs/Input';
import { convertDateTimeValuesInFilters } from '../list/components/ConditionParser';
import { dummyAppointment } from '@lmt/unicorn-shared/app/helpers/configure-react-query';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { getBGColor } from '../components/color-parser';
import { useEventCallback } from '@mui/material';
const ColorPickerWrapped = CustomPicker(StyledColorPicker);
export type Props = FormComponentProps & {
  rule?: AppointmentColorSettingsRuleDto;
};

type Form = Omit<
  AppointmentColorSettingsRuleDto,
  'position' | 'filterExpression'
>;

const nameOfAppointmentField = nameofFactory<VisitListDto>();
const nameOfPatientField = nameofFactory<PatientDto>();
const groupOperations: Array<'and' | 'or' | 'notAnd' | 'notOr'> = ['and', 'or'];
const filterOperations: dxFilterBuilderField['filterOperations'] = ['=', '<>'];
const dateFilterOperations: dxFilterBuilderField['filterOperations'] = [
  '=',
  '<>',
  '<',
  '<=',
  '>',
  '>=',
];
const hexReg = /^#[0-9A-F]{6}$/i;
const isColor = (color: string) => {
  return hexReg.test(color);
};

export const ColorRuleEditForm: React.FC<Props> = React.memo(
  function ColorRuleEditForm(props) {
    const i18next = useScopedTranslation('calendar.color_settings.form');
    const globalI18next = useTranslation();
    const dispatch = useUniDispatch();
    const isNightTheme = useUniSelector(getSettings).isNightTheme;
    const modals = useModal();
    const colorSettingsQuery =
      QueryFactory.AppointmentColorSettingsQuery.useGetQuery();
    const rule = props.rule;
    const [parsedFilter, setParsedFilter] = useState(() => {
      if (!rule?.filterExpression) return undefined;
      const parsedFilters = JSON.parse(rule.filterExpression);

      convertDateTimeValuesInFilters(parsedFilters, dummyAppointment);
      return parsedFilters;
    });
    const [hex, setHex] = useState<string>(rule?.color ?? '#ff0000');
    const handleFiltersChanged = useEventCallback((filter: any) => {
      setParsedFilter(filter.value);
    });

    const form = useForm<Form>({
      defaultValues: {
        ...rule,
      },
    });
    const visitTypes = useActiveVisitTypes();

    const queryCache = useQueryClient();
    const dxFields: dxFilterBuilderField[] = useMemo(() => {
      return [
        {
          dataType: 'number',
          dataField: nameOfAppointmentField('statusId'),
          caption: i18next.t('field_name.status_id'),
          lookup: {
            dataSource: Object.values(AppointmentStatusCodes)
              .filter((value) => typeof value === 'string')
              .map((x) => ({
                value: globalI18next.t(
                  `visit_status.${AppointmentStatusCodes[x as any]}` as any,
                ),
                id: AppointmentStatusCodes[x as any],
              })),
            displayExpr: 'value',
            valueExpr: 'id',
          },
          filterOperations,
        },
        {
          dataType: 'string',
          dataField: nameOfAppointmentField('title'),
          caption: i18next.t('field_name.title'),
        },
        {
          dataType: 'string',
          dataField: nameOfAppointmentField('note'),
          caption: i18next.t('field_name.note'),
        },
        {
          dataType: 'number',
          dataField: nameOfAppointmentField('typeId'),
          caption: i18next.t('field_name.visit_type'),
          lookup: {
            dataSource: visitTypes.map((x) => ({
              ...x,
              value: globalI18next.t(x.value as any),
            })),
            displayExpr: nameofFactory<DictionaryRecordDto>()('value'),
            valueExpr: nameofFactory<DictionaryRecordDto>()('id'),
          },
          filterOperations,
        },
        {
          dataType: 'datetime',
          dataField: nameOfAppointmentField('start'),
          caption: i18next.t('field_name.start'),
          filterOperations: dateFilterOperations,
        },
        {
          dataType: 'datetime',
          dataField: nameOfAppointmentField('end'),
          caption: i18next.t('field_name.end'),
          filterOperations: dateFilterOperations,
        },
        {
          dataType: 'boolean',
          dataField: nameOfPatientField('isPrivateInsurance'),
          caption: i18next.t('field_name.is_private_insurance'),
          filterOperations,
        },
        {
          dataType: 'boolean',
          dataField: nameOfPatientField('isSelfPayer'),
          caption: i18next.t('field_name.is_self_payer'),
          filterOperations,
        },
      ] as dxFilterBuilderField[];
    }, []);
    const [isSaving, setIsSaving] = useState(false);
    const onSave = useErrorHandler(
      async (_data: Form) => {
        setIsSaving(true);
        const patchDto = superjson.parse<typeof colorSettingsQuery.data>(
          superjson.stringify(colorSettingsQuery.data!),
        )!;

        try {
          if (rule) {
            // update existing rule
            const existingRule = patchDto.rules.find(
              (x) => x.position === rule.position,
            )!;
            existingRule.color = color;
            if (!existingRule.isDefault) {
              existingRule.filterExpression = parsedFilter
                ? JSON.stringify(parsedFilter)
                : null!;
            }
            await QueryFactory.AppointmentColorSettingsClient.patch(patchDto);
          } else {
            const updatedRules = patchDto.rules.map((rule) => ({
              ...rule,
              position: rule.position + 1,
            }));
            // add new rule
            updatedRules.push({
              color: color,
              filterExpression: parsedFilter
                ? JSON.stringify(parsedFilter)
                : null!,
              position: 1,
              isDefault: false,
            });
            try {
              await QueryFactory.AppointmentColorSettingsClient.patch({
                rules: updatedRules,
              });
            } catch (e) {
              throw e;
            }
          }
          await queryCache.invalidateQueries({
            queryKey: QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
          });
          props.closeForm(props.formId);
        } finally {
          setIsSaving(false);
        }
      },
      () => {
        /* we always show serverErrorsCombined, no need to set errors to Form */
      },
    );
    const [color, setColor] = useState(props.rule?.color ?? '#ff0000');

    const onDelete = useEventCallback(async () => {
      if (
        await modals.showConfirm({
          text: i18next.t('delete_confirmation_text'),
        })
      ) {
        setIsSaving(true);
        const patchDto = colorSettingsQuery.data!;

        try {
          if (rule) {
            patchDto.rules = patchDto.rules.filter(
              (x) => x.position !== rule.position,
            )!;
            await QueryFactory.AppointmentColorSettingsClient.patch(patchDto);
          }
          await queryCache.invalidateQueries({
            queryKey: QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
          });
          props.closeForm(props.formId);
        } finally {
          setIsSaving(false);
        }
      }
    });

    return (
      <div
        className={globalStyles.autoSizedContainer}
        data-test-id="colorRuleEditFormPopup"
      >
        <form
          onSubmit={form.handleSubmit(onSave.handler)}
          className={styles.form}
        >
          <Loading
            loading={isSaving || colorSettingsQuery.isLoading}
            title={i18next.t('saving')}
          >
            <div className={styles.dataContainer}>
              <div
                className={styles.filterColumn}
                data-test-id="conditions-area"
              >
                <div className={styles.header}>{i18next.t('conditions')}</div>
                <ErrorBoundary fallback={<div>Error showing filter</div>}>
                  <FilterBuilderLazy
                    className={styles.filter}
                    disabled={rule?.isDefault}
                    fields={dxFields}
                    allowHierarchicalFields={true}
                    groupOperations={groupOperations}
                    maxGroupLevel={1}
                    onValueChanged={handleFiltersChanged}
                    defaultValue={
                      parsedFilter
                    } /* we can't save custom objects in ReactForm, so we store filters outside of it */
                  />
                </ErrorBoundary>
              </div>
              <div className={styles.colorColumn}>
                <div className={styles.header}>{i18next.t('preview')}</div>
                <div className={styles.previewContainer}>
                  <PreviewVisit color={getBGColor(color, isNightTheme)} />
                </div>
                <div className={styles.header}>{i18next.t('color')}</div>
                <div className={styles.pickerContainer}>
                  <ColorPickerWrapped
                    color={color}
                    onChange={(newColor: any) => {
                      setColor(newColor.hex);
                      setHex(newColor.hex);
                    }}
                  />
                  <Input
                    className={styles.hexInput}
                    value={hex}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setHex(value);
                      if (isColor(value)) {
                        setColor(value);
                      }
                    }}
                    errorText={
                      !isColor(hex) ? i18next.t('invalid_hex_error') : undefined
                    }
                    testId="colour-input-field"
                  />
                </div>
              </div>
            </div>
            <div
              className={clsx(
                globalStyles.submitContainer,
                styles.controlsContainer,
              )}
            >
              <Error error={onSave.serverErrorsCombined} />
              {rule && !rule.isDefault && (
                <Button
                  className={globalStyles.submit}
                  title={i18next.t('delete_button')}
                  color={ButtonColor.Danger}
                  type={'button'}
                  onClick={onDelete}
                />
              )}
              <Button
                className={globalStyles.submit}
                title={i18next.t('save_button')}
                type={'submit'}
              />
            </div>
          </Loading>
        </form>
      </div>
    );
  },
);
